import { useInView } from "@/hooks/useInView";
import {
  MonitorSmartphone,
  Gauge,
  Type,
  SunMoon,
  Accessibility,
  Hand,
  EyeOff,
  Volume2,
  Trophy,
  Stethoscope,
  Lock,
  Clock,
  X,
  Check,
} from "lucide-react";

const PROMISES = [
  {
    icon: EyeOff,
    title: "No flashing or flickering",
    desc: "All animations are smooth, slow, and predictable",
  },
  {
    icon: Volume2,
    title: "No sudden sounds",
    desc: "Audio is always opt-in, never surprise",
  },
  {
    icon: Trophy,
    title: "No streaks or scores",
    desc: "We never create guilt for non-use",
  },
  {
    icon: Stethoscope,
    title: "No clinical language",
    desc: "Warm, human, and affirming — always",
  },
  {
    icon: Lock,
    title: "No data selling",
    desc: "Your information stays yours",
  },
  {
    icon: Clock,
    title: "No time pressure",
    desc: "Everything moves at your pace",
  },
];

const A11Y_FEATURES = [
  { icon: SunMoon, title: "Theme Options", desc: "Easier on sensitive eyes" },
  { icon: Gauge, title: "Reduce Motion", desc: "Slower, gentler animations" },
  { icon: Type, title: "Adjustable Text Size", desc: "Small to extra large" },
  {
    icon: MonitorSmartphone,
    title: "High Contrast",
    desc: "Enhanced visibility",
  },
  {
    icon: Accessibility,
    title: "Screen Reader Support",
    desc: "Full accessibility labels",
  },
  {
    icon: Hand,
    title: "Large Touch Targets",
    desc: "44pt minimum on all buttons",
  },
];

const COMPARISONS = [
  { not: "A therapy app", are: "A daily companion" },
  { not: "Gamified", are: "Calm and pressure-free" },
  { not: "Data-hungry", are: "Privacy-first" },
  { not: "One-size-fits-all", are: "Deeply customisable" },
  { not: "Clinical", are: "Warm, human, and affirming" },
];

export default function PhilosophySection() {
  const [promRef, promInView] = useInView();
  const [a11yRef, a11yInView] = useInView();
  const [compRef, compInView] = useInView();

  return (
    <>
      {/* Design Philosophy */}
      <section
        id="philosophy"
        data-testid="philosophy-section"
        className="py-20 md:py-32 px-6 md:px-12 lg:px-20 relative"
      >
        <div ref={promRef} className="max-w-7xl mx-auto">
          <div
            className={`text-center mb-16 transition-all duration-700 ${
              promInView
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-6"
            }`}
          >
            <p className="text-sm uppercase tracking-widest text-brand-rose font-semibold mb-4">
              Our commitment
            </p>
            <h2
              data-testid="philosophy-headline"
              className="font-heading text-3xl md:text-5xl font-bold text-[#2F3A56] leading-tight mb-4"
            >
              Designed with your nervous system in mind
            </h2>
            <p className="text-base md:text-lg text-[#5A6376] max-w-2xl mx-auto leading-relaxed">
              Every screen, every animation, every word in NeuroNest has been
              crafted to be sensory-safe. Here's what that means:
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {PROMISES.map((item, i) => (
              <div
                key={item.title}
                data-testid={`promise-card-${i}`}
                className={`flex items-start gap-4 bg-white rounded-2xl border border-[#E7E9EC] p-6 shadow-[0_2px_12px_rgba(0,0,0,0.03)] transition-all duration-500 ${
                  promInView
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-6"
                }`}
                style={{ transitionDelay: `${150 + i * 100}ms` }}
              >
                <div className="w-10 h-10 rounded-xl bg-brand-peach/15 flex items-center justify-center flex-shrink-0">
                  <item.icon
                    size={18}
                    className="text-[#D4956A]"
                    strokeWidth={1.5}
                  />
                </div>
                <div>
                  <h3 className="font-heading text-base font-semibold text-[#2F3A56] mb-1">
                    {item.title}
                  </h3>
                  <p className="text-sm text-[#5A6376] leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Accessibility */}
      <section
        id="accessibility"
        data-testid="accessibility-section"
        className="py-20 md:py-32 px-6 md:px-12 lg:px-20 bg-white relative"
      >
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#E7E9EC] to-transparent" />
        <div ref={a11yRef} className="max-w-7xl mx-auto">
          <div
            className={`text-center mb-16 transition-all duration-700 ${
              a11yInView
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-6"
            }`}
          >
            <p className="text-sm uppercase tracking-widest text-brand-blue font-semibold mb-4">
              Accessibility
            </p>
            <h2
              data-testid="accessibility-headline"
              className="font-heading text-3xl md:text-5xl font-bold text-[#2F3A56] leading-tight"
            >
              Built for everyone, customised for you
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {A11Y_FEATURES.map((item, i) => (
              <div
                key={item.title}
                data-testid={`a11y-card-${i}`}
                className={`text-center bg-[#F8F9FB] rounded-2xl p-7 transition-all duration-500 ${
                  a11yInView
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-6"
                }`}
                style={{ transitionDelay: `${150 + i * 100}ms` }}
              >
                <div className="w-12 h-12 rounded-2xl bg-brand-blue/10 flex items-center justify-center mx-auto mb-4">
                  <item.icon
                    size={22}
                    className="text-brand-blue"
                    strokeWidth={1.5}
                  />
                </div>
                <h3 className="font-heading text-base font-semibold text-[#2F3A56] mb-1">
                  {item.title}
                </h3>
                <p className="text-sm text-[#5A6376]">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What Makes Us Different */}
      <section
        data-testid="comparison-section"
        className="py-20 md:py-32 px-6 md:px-12 lg:px-20 relative"
      >
        <div ref={compRef} className="max-w-4xl mx-auto">
          <div
            className={`text-center mb-16 transition-all duration-700 ${
              compInView
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-6"
            }`}
          >
            <p className="text-sm uppercase tracking-widest text-brand-sage font-semibold mb-4">
              The difference
            </p>
            <h2
              data-testid="comparison-headline"
              className="font-heading text-3xl md:text-5xl font-bold text-[#2F3A56] leading-tight"
            >
              What makes us different
            </h2>
          </div>

          <div
            className={`space-y-4 transition-all duration-700 delay-200 ${
              compInView
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-6"
            }`}
          >
            {COMPARISONS.map((item, i) => (
              <div
                key={item.not}
                data-testid={`comparison-row-${i}`}
                className="flex flex-col sm:flex-row items-stretch sm:items-center bg-white rounded-2xl border border-[#E7E9EC] overflow-hidden shadow-[0_2px_12px_rgba(0,0,0,0.03)]"
              >
                <div className="flex-1 flex items-center gap-3 px-6 py-4 bg-brand-rose/5">
                  <div className="w-7 h-7 rounded-full bg-brand-rose/15 flex items-center justify-center flex-shrink-0">
                    <X size={14} className="text-brand-rose" strokeWidth={2} />
                  </div>
                  <span className="text-sm text-[#5A6376]">
                    We are <strong className="text-[#2F3A56]">not</strong>{" "}
                    {item.not.toLowerCase()}
                  </span>
                </div>
                <div className="w-px bg-[#E7E9EC] self-stretch hidden sm:block" />
                <div className="flex-1 flex items-center gap-3 px-6 py-4 bg-brand-sage/5">
                  <div className="w-7 h-7 rounded-full bg-brand-sage/20 flex items-center justify-center flex-shrink-0">
                    <Check
                      size={14}
                      className="text-[#7EAD87]"
                      strokeWidth={2}
                    />
                  </div>
                  <span className="text-sm text-[#5A6376]">
                    We <strong className="text-[#2F3A56]">are</strong>{" "}
                    {item.are.toLowerCase()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
